import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import apiClient from '../../api/client';

interface DashboardChartProps {
    title: string;
    metric: 'daily-sales' | 'daily-registrations';
    dataKey: string;
    color: string;
}

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        const data = payload[0];
        const isSales = data.name === 'Vendas';
        const value = isSales
            ? `R$ ${parseFloat(data.value).toFixed(2).replace('.', ',')}`
            : data.value;

        return (
            <div className="bg-white p-3 rounded-lg shadow-subtle border border-border-color">
                <p className="font-bold text-text-primary">{label}</p>
                <p style={{ color: data.stroke }}>
                    {data.name}: {value}
                </p>
            </div>
        );
    }
    return null;
};

const periods: (7 | 15 | 30 | 90)[] = [7, 15, 30, 90];

export function DashboardChart({ title, metric, dataKey, color }: DashboardChartProps) {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activePeriod, setActivePeriod] = useState<7 | 15 | 30 | 90>(7);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            setError('');
            try {
                const response = await apiClient.get(`/admin/dashboard/${metric}`, {
                    params: { days: activePeriod }
                });
                setData(response.data);
            } catch (err: any) {
                setError(err.response?.data?.error || `Falha ao carregar dados para ${title}.`);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [activePeriod, metric, title]);

    return (
        <div className="bg-white p-6 rounded-lg shadow-subtle min-h-[350px]">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-text-primary">{title}</h2>
                <div className="flex items-center gap-2 bg-background p-1 rounded-lg">
                    {periods.map(period => (
                        <button
                            key={period}
                            onClick={() => setActivePeriod(period)}
                            className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${
                                activePeriod === period
                                    ? 'bg-white text-brand-blue shadow-sm'
                                    : 'text-text-secondary hover:bg-gray-200'
                            }`}
                        >
                            {period} dias
                        </button>
                    ))}
                </div>
            </div>

            <div className="w-full min-h-[300px] h-72">
                {loading ? ( 
                    <div className="w-full h-full bg-gray-100 rounded-md animate-pulse"></div> 
                ) : error ? ( 
                    <div className="flex items-center justify-center h-full text-red-500">{error}</div> 
                ) : data.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <svg className="w-16 h-16 mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <p className="text-base font-medium text-gray-500">Nenhum dado registado neste período</p>
                        <p className="text-sm text-gray-400 mt-1">Selecione outro intervalo de tempo</p>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                            <defs>
                                <linearGradient id={`gradient-${dataKey.replace(/\s/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={color} stopOpacity={0.4}/>
                                    <stop offset="95%" stopColor={color} stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
                            <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                            <YAxis
                                stroke="#9ca3af"
                                fontSize={12}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(value) => dataKey === 'Vendas' ? `R$${value}` : `${value}`}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" name={dataKey} dataKey={dataKey} stroke={color} strokeWidth={2} fillOpacity={1} fill={`url(#gradient-${dataKey.replace(/\s/g, '')})`} />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}